import { APIProvider, authController } from "../../../../AppController";
import { APIRoutes } from "../../../../config";
import {
  IHealthScribeRepository,
  IGetSessionRequest,
  IGetSessionResponse,
  IListSessionsRequest,
  IListTranscriptionsRequest,
  IListTranscriptionsResponse,
  IS3MultipartUploadInit,
  IS3MultipartUploadInitResponse,
  IS3MultipartUploadPart,
  IS3MultipartUploadPartResponse,
  IS3MultipartUploadComplete,
  IS3MultipartUploadCompleteResponse,
  IS3MultipartUploadAbort,
  IS3UploadCompleteAudioRequest,
  IS3UploadCompleteAudioResponse,
  // V2 Types
  IStartRecordingV2Request,
  IStartRecordingV2Response,
  IStopRecordingV2Request,
  IStopRecordingV2Response,
  IBatchSaveV2Request,
  IBatchSaveV2Response,
  IGenerateNarrativeV2Request,
  IGenerateNarrativeV2Response,
  ICompleteSessionV2Request,
  ICompleteSessionV2Response,
  IGetSessionV2Response,
  IListSessionsV2Request,
  IListSessionsV2Response,
} from "../../../core/ports/HealthScribe";

const env = import.meta.env;

export const HealthScribeRepositoryFactory = ({
  apiProvider,
  authService,
}: {
  apiProvider: typeof APIProvider;
  authService: typeof authController;
}): IHealthScribeRepository => {
  const getAuthHeaders = async () => ({
    Authorization: `Bearer ${await authService.getAuthenticationToken()}`,
    "Content-Type": "application/json",
  });

  // NOTE: V1 startRecording and stopRecording functions removed - use V2 startRecordingV2 and stopRecordingV2

  const getSession = async (
    request: IGetSessionRequest
  ): Promise<IGetSessionResponse> => {
    try {
      const headers = await getAuthHeaders();
      const url = APIRoutes.healthScribeGetSession
        .replace("{clinic_id}", request.clinic_id.toString())
        .replace("{session_id}", request.session_id);
      const response = await apiProvider.GET(url, {}, headers);
      return response;
    } catch (error) {
      console.error("[HealthScribe Repository] Failed to get session:", error);
      throw error;
    }
  };

  const listSessions = async (
    request: IListSessionsRequest
  ): Promise<string> => {
    try {
      const headers = await getAuthHeaders();
      const url = APIRoutes.healthScribeListSessions.replace(
        "{clinic_id}",
        request.clinic_id.toString()
      );
      const queryParams = new URLSearchParams();
      if (request.patient_id)
        queryParams.append("patient_id", request.patient_id.toString());
      if (request.limit) queryParams.append("limit", request.limit.toString());
      if (request.offset)
        queryParams.append("offset", request.offset.toString());

      const finalUrl = queryParams.toString()
        ? `${url}?${queryParams.toString()}`
        : url;
      const response = await apiProvider.GET(finalUrl, {}, headers);
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to list sessions:",
        error
      );
      throw error;
    }
  };

  // S3 Multipart Upload methods
  const initS3MultipartUpload = async (
    request: IS3MultipartUploadInit
  ): Promise<IS3MultipartUploadInitResponse> => {
    try {
      const headers = await getAuthHeaders();
      const response = await apiProvider.POST(
        APIRoutes.healthScribeS3UploadInit,
        request,
        headers
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to init S3 multipart upload:",
        error
      );
      throw error;
    }
  };

  const uploadS3MultipartPart = async (
    request: IS3MultipartUploadPart
  ): Promise<IS3MultipartUploadPartResponse> => {
    try {
      const formData = new FormData();
      formData.append("session_id", request.session_id);
      formData.append("clinic_id", request.clinic_id.toString());
      formData.append("upload_id", request.upload_id);
      formData.append("part_number", request.part_number.toString());
      formData.append("file", request.file);

      // Use raw fetch for FormData since APIProvider might not handle it properly
      const authToken = await authService.getAuthenticationToken();

      const response = await fetch(
        `${env.VITE_API_GATEWAY_URL}${APIRoutes.healthScribeS3UploadPart}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to upload S3 multipart part:",
        error
      );
      throw error;
    }
  };

  const completeS3MultipartUpload = async (
    request: IS3MultipartUploadComplete
  ): Promise<IS3MultipartUploadCompleteResponse> => {
    try {
      const headers = await getAuthHeaders();
      const response = await apiProvider.POST(
        APIRoutes.healthScribeS3UploadComplete,
        request,
        headers
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to complete S3 multipart upload:",
        error
      );
      throw error;
    }
  };

  const abortS3MultipartUpload = async (
    request: IS3MultipartUploadAbort
  ): Promise<void> => {
    try {
      const headers = await getAuthHeaders();
      const url = APIRoutes.healthScribeS3UploadAbort
        .replace("{clinic_id}", request.clinic_id.toString())
        .replace("{session_id}", request.session_id)
        .replace("{upload_id}", request.upload_id);
      await apiProvider.DELETE(url, {}, headers);
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to abort S3 multipart upload:",
        error
      );
      throw error;
    }
  };

  const uploadCompleteAudio = async (
    request: IS3UploadCompleteAudioRequest
  ): Promise<IS3UploadCompleteAudioResponse> => {
    try {
      const formData = new FormData();
      formData.append("session_id", request.session_id);
      formData.append("clinic_id", request.clinic_id.toString());
      formData.append("filename", request.filename);
      formData.append("content_type", request.content_type);
      formData.append("file", request.audio_file);

      const response = await apiProvider.POST(
        APIRoutes.healthScribeS3UploadCompleteAudio,
        formData,
        {
          Authorization: `Bearer ${await authService.getAuthenticationToken()}`,
          "Content-Type": "multipart/form-data",
        }
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to upload complete audio:",
        error
      );
      throw error;
    }
  };

  // New Transcription List and Complete Methods
  const listTranscriptions = async (
    request: IListTranscriptionsRequest
  ): Promise<IListTranscriptionsResponse> => {
    try {
      const headers = await getAuthHeaders();
      const queryParams = new URLSearchParams();
      queryParams.append("patient_id", request.patient_id.toString());
      queryParams.append("clinic_id", request.clinic_id.toString());

      const url = `${
        APIRoutes.healthScribeTranscriptionList
      }?${queryParams.toString()}`;
      const response = await apiProvider.GET(url, {}, headers);
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to list transcriptions:",
        error
      );
      throw error;
    }
  };

  // V1 completeTranscriptionGenerateNarrative removed - use V2 generateNarrativeV2

  // ==================== V2 Multi-Segment Methods ====================

  const startRecordingV2 = async (
    request: IStartRecordingV2Request
  ): Promise<IStartRecordingV2Response> => {
    try {
      const headers = await getAuthHeaders();
      const response = await apiProvider.POST(
        APIRoutes.healthScribeV2StartRecording,
        request,
        headers
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository V2] Failed to start recording:",
        error
      );
      throw error;
    }
  };

  const stopRecordingV2 = async (
    request: IStopRecordingV2Request
  ): Promise<IStopRecordingV2Response> => {
    try {
      const headers = await getAuthHeaders();
      const response = await apiProvider.POST(
        APIRoutes.healthScribeV2StopRecording,
        request,
        headers
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository V2] Failed to stop recording:",
        error
      );
      throw error;
    }
  };

  const batchSaveTranscriptionsV2 = async (
    request: IBatchSaveV2Request
  ): Promise<IBatchSaveV2Response> => {
    try {
      const headers = await getAuthHeaders();
      const response = await apiProvider.POST(
        APIRoutes.healthScribeV2TranscriptionBatchSave,
        request,
        headers
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository V2] Failed to batch save transcriptions:",
        error
      );
      throw error;
    }
  };

  const generateNarrativeV2 = async (
    request: IGenerateNarrativeV2Request
  ): Promise<IGenerateNarrativeV2Response> => {
    try {
      const headers = await getAuthHeaders();
      const response = await apiProvider.POST(
        APIRoutes.healthScribeV2NarrativeGenerate,
        request,
        headers
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository V2] Failed to generate narrative:",
        error
      );
      throw error;
    }
  };

  const completeSessionV2 = async (
    request: ICompleteSessionV2Request
  ): Promise<ICompleteSessionV2Response> => {
    try {
      const headers = await getAuthHeaders();
      const response = await apiProvider.POST(
        APIRoutes.healthScribeV2SessionComplete,
        request,
        headers
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository V2] Failed to complete session:",
        error
      );
      throw error;
    }
  };

  const getSessionV2 = async (
    request: IGetSessionRequest
  ): Promise<IGetSessionV2Response> => {
    try {
      const headers = await getAuthHeaders();
      const url = APIRoutes.healthScribeV2GetSession.replace(
        "{session_id}",
        request.session_id
      );
      const response = await apiProvider.GET(
        url,
        { clinic_id: request.clinic_id },
        headers
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository V2] Failed to get session:",
        error
      );
      throw error;
    }
  };

  const listSessionsV2 = async (
    request: IListSessionsV2Request
  ): Promise<IListSessionsV2Response> => {
    try {
      const headers = await getAuthHeaders();
      const url = APIRoutes.healthScribeV2ListSessions.replace(
        "{clinic_id}",
        request.clinic_id.toString()
      );
      const response = await apiProvider.GET(
        url,
        {
          patient_id: request.patient_id,
          ...(request.status && { status: request.status }),
        },
        headers
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository V2] Failed to list sessions:",
        error
      );
      throw error;
    }
  };

  // Get Heidi transcribe-and-generate result status
  const getHeidiTranscribeAndGenerateResult = async (
    sessionId: string,
    clinicId: number,
    patientId: number
  ): Promise<{
    status?:
      | "processing"
      | "transcription_completed"
      | "generation_completed"
      | "failed";
    transcription_status?: "pending" | "processing" | "completed" | "failed";
    generation_status?: "pending" | "processing" | "completed" | "failed";
    narrative?: string;
    narrative_generated_at?: string;
    transcription_completed_at?: string;
    message?: string;
    error?: string;
  } | null> => {
    try {
      const headers = await getAuthHeaders();
      const url =
        APIRoutes.healthScribeHeidiTranscribeAndGenerateResult.replace(
          "{session_id}",
          sessionId
        );
      const response = await apiProvider.GET(
        url,
        { clinic_id: clinicId, patient_id: patientId },
        headers
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to get Heidi transcribe-and-generate result:",
        error
      );
      // Return null for 404 or other errors - means no processing exists
      return null;
    }
  };

  return {
    // Shared Methods (used by both V1 legacy and V2)
    getSession,
    listSessions,
    listTranscriptions,
    initS3MultipartUpload,
    uploadS3MultipartPart,
    completeS3MultipartUpload,
    abortS3MultipartUpload,
    uploadCompleteAudio,
    // V2 Methods
    startRecordingV2,
    stopRecordingV2,
    batchSaveTranscriptionsV2,
    generateNarrativeV2,
    completeSessionV2,
    getSessionV2,
    listSessionsV2,
    // Heidi Methods
    getHeidiTranscribeAndGenerateResult,
  };
};
